// composables/useDealScoring.ts
import { ref, reactive } from 'vue'
import { scoringService } from '@/services/scoringService'

interface StepResult {
  status: 'not-started' | 'processing' | 'completed' | 'not-completed' | 'error'
  data: any
  error?: string
}

interface ScoringResults {
  dealOrchestration: StepResult
  claimsVerification: StepResult
  regexExtraction: StepResult
  postClassification: StepResult
  spacyExtraction: StepResult
  glinerExtraction: StepResult
  urlExpansion: StepResult
  postToDeal: StepResult
  productDetails: StepResult
  ingestion: StepResult
  normalization: StepResult
  category: StepResult
  audience: StepResult
  priceIntel: StepResult
  brandIntel: StepResult
  scoring: StepResult
  tagging: StepResult
  leaderboards: StepResult
  indexing: StepResult
  processingTime: number
}

export function useDealScoring() {
  const isProcessing = ref(false)
  const currentPostText = ref<string>('')
  
  const results = ref<ScoringResults>({
    dealOrchestration: { status: 'not-started', data: null },
    claimsVerification: { status: 'not-started', data: null },
    regexExtraction: { status: 'not-started', data: null },
    postClassification: { status: 'not-started', data: null },
    spacyExtraction: { status: 'not-started', data: null },
    glinerExtraction: { status: 'not-started', data: null },
    urlExpansion: { status: 'not-started', data: null },
    postToDeal: { status: 'not-started', data: null },
    productDetails: { status: 'not-started', data: null },
    ingestion: { status: 'not-started', data: null },
    normalization: { status: 'not-started', data: null },
    category: { status: 'not-started', data: null },
    audience: { status: 'not-started', data: null },
    priceIntel: { status: 'not-started', data: null },
    brandIntel: { status: 'not-started', data: null },
    scoring: { status: 'not-started', data: null },
    tagging: { status: 'not-started', data: null },
    leaderboards: { status: 'not-started', data: null },
    indexing: { status: 'not-started', data: null },
    processingTime: 0
  })

  const resetResults = () => {
    results.value = {
      dealOrchestration: { status: 'not-started', data: null },
      claimsVerification: { status: 'not-started', data: null },
      regexExtraction: { status: 'not-started', data: null },
      postClassification: { status: 'not-started', data: null },
      spacyExtraction: { status: 'not-started', data: null },
      glinerExtraction: { status: 'not-started', data: null },
      urlExpansion: { status: 'not-started', data: null },
      postToDeal: { status: 'not-started', data: null },
      productDetails: { status: 'not-started', data: null },
      ingestion: { status: 'not-started', data: null },
      normalization: { status: 'not-started', data: null },
      category: { status: 'not-started', data: null },
      audience: { status: 'not-started', data: null },
      priceIntel: { status: 'not-started', data: null },
      brandIntel: { status: 'not-started', data: null },
      scoring: { status: 'not-started', data: null },
      tagging: { status: 'not-started', data: null },
      leaderboards: { status: 'not-started', data: null },
      indexing: { status: 'not-started', data: null },
      processingTime: 0
    }
  }

  const processPost = async (postText: string) => {
    resetResults()
    currentPostText.value = postText
    isProcessing.value = true
    const startTime = Date.now()

    try {
      // Step 0: Deal Orchestration (URL extraction -> expansion -> retailer detection -> product details)
      results.value.dealOrchestration.status = 'processing'
      await delay(200)
      const orchestrationResult = await scoringService.processPostToDeal(postText)
      results.value.dealOrchestration = {
        status: orchestrationResult && orchestrationResult.success ? 'completed' : 'not-completed',
        data: orchestrationResult,
        error: orchestrationResult?.error
      }

      // If orchestration succeeded, mark it as completed
      if (!orchestrationResult || !orchestrationResult.success) {
        // Orchestration failed, mark as error
        results.value.dealOrchestration.status = 'error'
      }

      // Step 9: Category Placement (not completed yet)
      results.value.category.status = 'not-completed'
      results.value.category.data = null

      // Step 10: Audience Assignment (not completed yet)
      results.value.audience.status = 'not-completed'
      results.value.audience.data = null

      // Step 11: Price Intelligence (not completed yet)
      results.value.priceIntel.status = 'not-completed'
      results.value.priceIntel.data = null

      // Step 12: Brand Intelligence (not completed yet)
      results.value.brandIntel.status = 'not-completed'
      results.value.brandIntel.data = null

      // Step 13: Scoring (not completed yet)
      results.value.scoring.status = 'not-completed'
      results.value.scoring.data = null

      // Step 14: Tagging (not completed yet)
      results.value.tagging.status = 'not-completed'
      results.value.tagging.data = null

      // Step 15: Leaderboards (not completed yet)
      results.value.leaderboards.status = 'not-completed'
      results.value.leaderboards.data = null

      // Step 16: Indexing (not completed yet)
      results.value.indexing.status = 'not-completed'
      results.value.indexing.data = null

      const endTime = Date.now()
      results.value.processingTime = endTime - startTime

    } catch (error: any) {
      console.error('Error processing deal:', error)
      // Mark current step as error
      type StepResultKey = Exclude<keyof ScoringResults, 'processingTime'>
      const stepKeys: StepResultKey[] = [
        'dealOrchestration', 'claimsVerification', 'regexExtraction', 'postClassification', 'spacyExtraction', 'glinerExtraction',
        'urlExpansion', 'productDetails', 'ingestion', 'normalization',
        'category', 'audience', 'priceIntel', 'brandIntel',
        'scoring', 'tagging', 'leaderboards', 'indexing'
      ]
      for (const key of stepKeys) {
        const stepResult = results.value[key]
        if (stepResult.status === 'processing') {
          results.value[key] = {
            status: 'error',
            data: null,
            error: error.message
          }
        }
      }
    } finally {
      isProcessing.value = false
    }
  }

  // Individual step functions
  const runRegexExtraction = async (postText?: string) => {
    const text = postText || currentPostText.value
    if (!text) {
      alert('Please provide post text or run the full pipeline first')
      return
    }
    currentPostText.value = text
    results.value.regexExtraction.status = 'processing'
    try {
      await delay(200)
      const regexResult = await scoringService.extractUrlsWithRegex(text)
      results.value.regexExtraction = {
        status: regexResult && regexResult.success ? 'completed' : 'not-completed',
        data: regexResult
      }
    } catch (error: any) {
      results.value.regexExtraction = {
        status: 'error',
        data: null,
        error: error.message
      }
    }
  }

  const runPostClassification = async (postText?: string) => {
    const text = postText || currentPostText.value
    if (!text) {
      alert('Please provide post text or run the full pipeline first')
      return
    }
    currentPostText.value = text
    results.value.postClassification.status = 'processing'
    try {
      await delay(1000)
      const classificationResult = await scoringService.classifyPost(text)
      results.value.postClassification = {
        status: classificationResult ? 'completed' : 'not-completed',
        data: classificationResult
      }
    } catch (error: any) {
      results.value.postClassification = {
        status: 'error',
        data: null,
        error: error.message
      }
    }
  }

  const runSpacyExtraction = async (postText?: string) => {
    const text = postText || currentPostText.value
    if (!text) {
      alert('Please provide post text or run the full pipeline first')
      return
    }
    results.value.spacyExtraction.status = 'processing'
    try {
      await delay(800)
      const spacyResult = await scoringService.extractWithSpacy(text)
      results.value.spacyExtraction = {
        status: spacyResult && spacyResult.success ? 'completed' : 'not-completed',
        data: spacyResult
      }
    } catch (error: any) {
      results.value.spacyExtraction = {
        status: 'error',
        data: null,
        error: error.message
      }
    }
  }

  const runGlinerExtraction = async (postText?: string) => {
    const text = postText || currentPostText.value
    if (!text) {
      alert('Please provide post text or run the full pipeline first')
      return
    }
    results.value.glinerExtraction.status = 'processing'
    try {
      await delay(800)
      const glinerResult = await scoringService.extractWithGliner(text)
      results.value.glinerExtraction = {
        status: glinerResult && glinerResult.success ? 'completed' : 'not-completed',
        data: glinerResult
      }
    } catch (error: any) {
      results.value.glinerExtraction = {
        status: 'error',
        data: null,
        error: error.message
      }
    }
  }

  const runUrlExpansion = async (url?: string) => {
    const targetUrl = url || 
      results.value.regexExtraction.data?.urls?.[0] ||
      results.value.spacyExtraction.data?.urls?.[0]
    
    if (!targetUrl) {
      alert('Please provide a URL or run regex/spaCy extraction first')
      return
    }
    results.value.urlExpansion.status = 'processing'
    try {
      await delay(500)
      const urlExpansionResult = await scoringService.expandUrl(targetUrl)
      results.value.urlExpansion = {
        status: urlExpansionResult && urlExpansionResult.success ? 'completed' : 'not-completed',
        data: urlExpansionResult
      }
    } catch (error: any) {
      results.value.urlExpansion = {
        status: 'error',
        data: null,
        error: error.message
      }
    }
  }

  const runProductDetails = async (url?: string) => {
    // Prefer expanded URL, but fall back to original extracted URLs
    const targetUrl = url || 
      results.value.urlExpansion.data?.finalUrl ||
      results.value.regexExtraction.data?.urls?.[0] ||
      results.value.spacyExtraction.data?.urls?.[0]
    
    if (!targetUrl) {
      alert('Please provide a URL or run URL extraction/expansion first')
      return
    }

    // If we have an expanded URL, use it; otherwise try to expand first
    let urlToUse = targetUrl
    if (!results.value.urlExpansion.data?.finalUrl && targetUrl) {
      // Try to expand the URL first if expansion hasn't been run
      try {
        const expansionResult = await scoringService.expandUrl(targetUrl)
        if (expansionResult && expansionResult.success && expansionResult.finalUrl) {
          urlToUse = expansionResult.finalUrl
        }
      } catch {
        // If expansion fails, continue with original URL
      }
    }

    results.value.productDetails.status = 'processing'
    try {
      await delay(1000)
      const productDetailsResult = await scoringService.getProductDetails(urlToUse)
      results.value.productDetails = {
        status: productDetailsResult && productDetailsResult.success ? 'completed' : 'not-completed',
        data: productDetailsResult
      }
    } catch (error: any) {
      results.value.productDetails = {
        status: 'error',
        data: {
          success: false,
          error: error.message || 'Unknown error occurred',
          retailer: 'Unknown'
        },
        error: error.message
      }
    }
  }

  const runIngestion = async () => {
    const classificationResult = results.value.postClassification.data
    const productDetailsResult = results.value.productDetails.data
    const urlExpansionResult = results.value.urlExpansion.data
    const extractedUrl = results.value.regexExtraction.data?.urls?.[0] ||
                         results.value.spacyExtraction.data?.urls?.[0]

    if (!productDetailsResult && !urlExpansionResult && !extractedUrl) {
      alert('Please run Product Details extraction first')
      return
    }

    const deal = {
      title: productDetailsResult?.title || classificationResult?.product_name || '',
      brand: productDetailsResult?.brand || '',
      retailer: productDetailsResult?.retailer || classificationResult?.retailer || '',
      price: parseFloat(classificationResult?.price?.replace('$', '') || productDetailsResult?.price?.replace('$', '') || '0'),
      url: productDetailsResult?.productUrl || urlExpansionResult?.finalUrl || extractedUrl
    }

    results.value.ingestion.status = 'processing'
    try {
      await delay(500)
      const ingestionResult = await scoringService.ingestDeal(deal)
      results.value.ingestion = {
        status: ingestionResult ? 'completed' : 'error',
        data: ingestionResult
      }
    } catch (error: any) {
      results.value.ingestion = {
        status: 'error',
        data: null,
        error: error.message
      }
    }
  }

  const runNormalization = async () => {
    const classificationResult = results.value.postClassification.data
    const productDetailsResult = results.value.productDetails.data
    const urlExpansionResult = results.value.urlExpansion.data
    const extractedUrl = results.value.regexExtraction.data?.urls?.[0] ||
                         results.value.spacyExtraction.data?.urls?.[0]

    if (!productDetailsResult && !urlExpansionResult && !extractedUrl) {
      alert('Please run Product Details extraction first')
      return
    }

    const deal = {
      title: productDetailsResult?.title || classificationResult?.product_name || '',
      brand: productDetailsResult?.brand || '',
      retailer: productDetailsResult?.retailer || classificationResult?.retailer || '',
      price: parseFloat(classificationResult?.price?.replace('$', '') || productDetailsResult?.price?.replace('$', '') || '0'),
      url: productDetailsResult?.productUrl || urlExpansionResult?.finalUrl || extractedUrl
    }

    results.value.normalization.status = 'processing'
    try {
      await delay(800)
      const normalizationResult = await scoringService.normalizeDeal(deal)
      results.value.normalization = {
        status: normalizationResult ? 'completed' : 'error',
        data: normalizationResult
      }
    } catch (error: any) {
      results.value.normalization = {
        status: 'error',
        data: null,
        error: error.message
      }
    }
  }

  const runPostToDeal = async (postText?: string) => {
    const text = postText || currentPostText.value
    if (!text || !text.trim()) {
      alert('Please provide post text or run the full pipeline first')
      return
    }
    results.value.postToDeal.status = 'processing'
    try {
      await delay(1000)
      const postToDealResult = await scoringService.processPostToDeal(text)
      results.value.postToDeal = {
        status: postToDealResult && postToDealResult.success ? 'completed' : 'not-completed',
        data: postToDealResult
      }
    } catch (error: any) {
      results.value.postToDeal = {
        status: 'error',
        data: null,
        error: error.message
      }
    }
  }

  return {
    results,
    isProcessing,
    processPost,
    resetResults,
    runRegexExtraction,
    runPostClassification,
    runSpacyExtraction,
    runGlinerExtraction,
    runUrlExpansion,
    runPostToDeal,
    runProductDetails,
    runIngestion,
    runNormalization,
    currentPostText
  }
}

// Helper function for delays
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}
